import type { NextPageWithLayout } from 'next';

import { HomepageFaq } from '@/components/homepage';
import ShopLayout from '@/components/layouts/ShopLayout';
import StartJourneyCTA from '@/components/sections/StartJourneyCTA';
import { YotpoReviews } from '@/components/sections/YotpoReviews';
import { AlterTestDrive } from '@/components/why-alter/AlterTestDrive';
import BackedByExperts from '@/components/why-alter/BackedByExperts';
import DrivenByScience from '@/components/why-alter/DrivenByScience';
import Equipment from '@/components/why-alter/Equipment';
import { HeroSection } from '@/components/why-alter/HeroSection';
import OneSizeFitsNone from '@/components/why-alter/OneSizeFitsNone';
import PersonalizedForEveryBody from '@/components/why-alter/PersonalizedForEveryBody';
import Testimonies from '@/components/why-alter/Testimonies';
import {
  getAlterExperts,
  getCard,
  getCardRepeater,
  getGallery,
  getMedia,
  getWhyAlterFAQ,
} from '@/graphql/contentful';
import { addApolloState, initializeApollo } from '@/lib/apollo/apollo-client';
import type { CardT, ExpertT, ImageT, MediaT, TextT } from '@/utils/contentfulTypes';

export async function getStaticProps() {
  const apolloClient = initializeApollo();

  const hero = await getMedia('17TijTXs2szGTScng8CHbB');
  const oneSizeFitsNone = await getCardRepeater('2qUjtjlVAxSPhPD9IvxvnU');
  const personalizedForEveryBody = await getCardRepeater('2Rv5qpFA0CT36k4XVt0K2P');
  const alterExperts = await getAlterExperts();
  const alterExpertsCompanies = await getGallery('4va2IhKaGxh4qb9wOEk5Ch');
  const alterTestDrive = await getCard('2wwSe6JLDpj1lDdsFnvOfm');
  const testimonies = await getCardRepeater('310CCEDpWhnwdEK3x1ZPpx');
  const faq = await getWhyAlterFAQ();

  return addApolloState(apolloClient, {
    props: {
      hero,
      oneSizeFitsNone: oneSizeFitsNone.repeater?.itemCollection?.items,
      personalizedForEveryBody: personalizedForEveryBody.repeater?.itemCollection?.items,
      alterExperts: alterExperts.repeater,
      alterExpertsCompanies: alterExpertsCompanies?.imagesCollection?.items,
      alterTestDrive,
      testimonies: testimonies.repeater?.itemCollection?.items,
      faq,
    },
    revalidate: 300, // 5 minutes
  });
}

interface WhyAlterProps {
  hero: MediaT;
  oneSizeFitsNone: CardT[];
  personalizedForEveryBody: CardT[];
  alterExperts: {
    headline: string;
    itemCollection: {
      items: ExpertT[];
    };
  };
  alterExpertsCompanies: ImageT[];
  alterTestDrive: CardT;
  testimonies: CardT[];
  faq: TextT[];
}

const WhyAlter: NextPageWithLayout<WhyAlterProps> = ({
  hero,
  oneSizeFitsNone,
  personalizedForEveryBody,
  alterExperts,
  alterExpertsCompanies,
  alterTestDrive,
  testimonies,
  faq,
}) => {
  return (
    <div className="text-alter-black">
      <HeroSection data={hero} />
      <DrivenByScience />
      <OneSizeFitsNone data={oneSizeFitsNone} />
      <PersonalizedForEveryBody data={personalizedForEveryBody} />
      <BackedByExperts
        data={alterExperts.itemCollection.items}
        title={alterExperts.headline}
        logos={alterExpertsCompanies}
      />
      <Equipment />
      <AlterTestDrive data={alterTestDrive} />
      <Testimonies data={testimonies} />

      <YotpoReviews />
      <div className="bg-white">
        <HomepageFaq items={faq} />
      </div>
      <StartJourneyCTA variant="dna" topCornersColor="#F6F6F1" />
    </div>
  );
};

WhyAlter.getLayout = (page) => {
  return (
    <>
      <ShopLayout fill dark hideBlurAtTop>
        {page}
      </ShopLayout>
    </>
  );
};

export default WhyAlter;
